import { createFileRoute } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { DisplayNamesLastFirstContext } from '@apps/web/context/display_names_last_first_context';
import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { useSessionState } from '@apps/web/context/session_state_provider';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useOrganizationEvents } from '@apps/web/hooks/use_organization_events';
import { isGeneralAgentLifecycleEligible } from '@apps/web/lib/chat_access';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { ModeOverrideProvider } from '@apps/web/lib/mode_store';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';
import { getUserName } from '@apps/web/routes/_app/(extension)/extension/client_feed_filters';
import {
    ExtensionAgentChat,
    useExtensionAgentChat,
} from '@apps/web/routes/_app/(extension)/extension/extension_agent_chat';
import { ExtensionConversationWorkspace } from '@apps/web/routes/_app/(extension)/extension/extension_conversation_workspace';
import { ExtensionOutreachesTab } from '@apps/web/routes/_app/(extension)/extension/extension_outreaches_tab';
import {
    describeUnknownError,
    logExtensionPageEvent,
} from '@apps/web/routes/_app/(extension)/extension/extension_page_logging';
import { ExtensionPageShiftsTab } from '@apps/web/routes/_app/(extension)/extension/extension_page_shift_cards';
import {
    type ExtensionPageShift,
    type PageInfo,
    type SelectedExtensionShift,
} from '@apps/web/routes/_app/(extension)/extension/extension_page_shifts';
import { ExtensionSelectedShiftsSection } from '@apps/web/routes/_app/(extension)/extension/extension_selected_shifts_section';
import { ExtensionSettingsDialog } from '@apps/web/routes/_app/(extension)/extension/extension_settings_dialog';
import { ExtensionSidebarHeader } from '@apps/web/routes/_app/(extension)/extension/extension_sidebar_header';
import { EXTENSION_CARD_CLASS } from '@apps/web/routes/_app/(extension)/extension/extension_styles';
import {
    useClientShiftContext,
    useSelectedClientShiftIds,
} from '@apps/web/routes/_app/(extension)/extension/use_client_shift_context';
import { useClientSync } from '@apps/web/routes/_app/(extension)/extension/use_client_sync';
import { useContextualShift } from '@apps/web/routes/_app/(extension)/extension/use_contextual_shift';
import { useEhrSelectedShiftResolution } from '@apps/web/routes/_app/(extension)/extension/use_ehr_selected_shift_resolution';
import { useExtensionContextAnalytics } from '@apps/web/routes/_app/(extension)/extension/use_extension_context_analytics';
import {
    type ExtensionOrganization,
    useExtensionOrganization,
} from '@apps/web/routes/_app/(extension)/extension/use_extension_organization';
import { useExtensionOutreachFeed } from '@apps/web/routes/_app/(extension)/extension/use_extension_outreach_feed';
import {
    type ExtensionSidepanelConfigPayload,
    useExtensionParentMessages,
} from '@apps/web/routes/_app/(extension)/extension/use_extension_parent_messages';
import { useExtensionResolvedContext } from '@apps/web/routes/_app/(extension)/extension/use_extension_resolved_context';
import {
    type ExtensionSidebarTab,
    useExtensionSidebarNavigation,
} from '@apps/web/routes/_app/(extension)/extension/use_extension_sidebar_navigation';
import {
    useExtensionSidebarPrefs,
    usePublishExtensionSidebarPrefs,
} from '@apps/web/routes/_app/(extension)/extension/use_extension_sidebar_prefs';
import { useOpenInPhoebe } from '@apps/web/routes/_app/(extension)/extension/use_open_in_phoebe';
import { usePageShiftResolvedMap } from '@apps/web/routes/_app/(extension)/extension/use_page_shift_resolved_map';
import { usePageShiftSelection } from '@apps/web/routes/_app/(extension)/extension/use_page_shift_selection';
import { usePublishedShiftActions } from '@apps/web/routes/_app/(extension)/extension/use_published_shift_actions';
import { useSidebarConversations } from '@apps/web/routes/_app/(extension)/extension/use_sidebar_conversations';
import {
    useSidebarRealtimeRefresh,
    useSidebarRefresh,
} from '@apps/web/routes/_app/(extension)/extension/use_sidebar_refresh';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import { useExtensionConfig } from '@apps/web/swr_data_fetching/extension_queries';
import { useHomeFeed } from '@apps/web/swr_data_fetching/home_queries';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';
import { EXTENSION_BOTTOM_BAR_ENABLED } from '@libraries/typescript/utilities/extension_surface_flags';

export const Route = createFileRoute('/_app/(extension)/extension')({
    component: ExtensionPage,
    head: () => ({
        meta: [{ title: 'Extension | Phoebe' }],
    }),
});

const CLIENT_SHIFT_TIME_REFRESH_MS = 60_000;
const EXTENSION_AUTH_REFRESH_REQUEST = 'EXTENSION_AUTH_REFRESH_REQUEST';

function ExtensionLoadingState() {
    return (
        <div className="bg-muted/20 flex min-h-screen items-center justify-center p-4">
            <div className="bg-background text-muted-foreground flex items-center gap-2 rounded-xl border px-4 py-3 text-sm shadow-sm">
                <Spinner />
                <span>Loading Phoebe…</span>
            </div>
        </div>
    );
}

function requestExtensionAuthRefresh() {
    window.parent.postMessage({ type: EXTENSION_AUTH_REFRESH_REQUEST }, '*');
}

function ExtensionSignInState() {
    const [hasOpenedSignInTab, setHasOpenedSignInTab] = useState(false);

    function openSignInTab() {
        const signInWindow = window.open(
            '/sign-in',
            '_blank',
            'noopener,noreferrer',
        );

        if (signInWindow !== null) {
            setHasOpenedSignInTab(true);
        }
    }

    return (
        <div className="bg-muted/20 flex min-h-screen items-center justify-center p-4">
            <div className="bg-background w-full max-w-sm space-y-4 rounded-[24px] border p-5 shadow-sm">
                <div className="space-y-2">
                    <h1 className="text-base font-semibold text-balance">
                        Sign in to Phoebe
                    </h1>
                    <p className="text-muted-foreground text-sm leading-6">
                        Complete sign in in a Phoebe browser tab, then refresh
                        the sidebar session here.
                    </p>
                </div>
                <div className="grid gap-2">
                    <Button onClick={openSignInTab}>
                        {hasOpenedSignInTab
                            ? 'Open sign-in again'
                            : 'Open sign-in tab'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={requestExtensionAuthRefresh}
                    >
                        Refresh sidebar session
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ExtensionPage() {
    const { initialized, session } = useSessionState();

    useEffect(() => {
        logExtensionPageEvent('Extension gate state changed', {
            initialized,
            userId: session?.identity.id ?? null,
        });
    }, [initialized, session?.identity.id]);

    useEffect(() => {
        const handleWindowError = (event: ErrorEvent) => {
            console.error('[Phoebe][ExtensionPage] Window error', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                error: event.error,
            });
        };
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            console.error(
                '[Phoebe][ExtensionPage] Unhandled promise rejection',
                {
                    reason: event.reason,
                },
            );
        };

        window.addEventListener('error', handleWindowError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleWindowError);
            window.removeEventListener(
                'unhandledrejection',
                handleUnhandledRejection,
            );
        };
    }, []);

    if (!initialized) {
        return <ExtensionLoadingState />;
    }

    if (!session) {
        return <ExtensionSignInState />;
    }

    return (
        <ModeOverrideProvider mode="live">
            <AuthenticatedExtensionPage />
        </ModeOverrideProvider>
    );
}

function ExtensionInitializationError() {
    return (
        <div className="bg-muted/20 min-h-screen p-4">
            <div className={cn(EXTENSION_CARD_CLASS, 'space-y-2 p-4')}>
                <h1 className="text-foreground text-sm font-semibold">
                    Could not initialize the sidebar
                </h1>
                <p className="text-muted-foreground text-sm">
                    Phoebe could not determine your signed-in user or active
                    organization.
                </p>
            </div>
        </div>
    );
}

// Prefer the organization record's own name, then what the extension config
// or the membership list knows about it.
function getResolvedOrganizationName(
    organization: ReturnType<typeof useOrganization>['data'],
    extensionConfig: ReturnType<typeof useExtensionConfig>['data'],
    {
        membershipOrganizationName,
        activeMembership,
        activeOrganizationLabel,
    }: ExtensionOrganization,
): string | null {
    return (
        organization?.name?.trim() ||
        extensionConfig?.organization_name?.trim() ||
        membershipOrganizationName ||
        (activeMembership ? activeOrganizationLabel : null)
    );
}

// Leave room at the bottom of the sidebar for whichever fixed bar is showing:
// the client quick-callout bar or the "Ask Phoebe" bar.
function getMainClassName({
    activeTab,
    isClientQuickCalloutBarVisible,
    isAgentChatBarVisible,
}: {
    readonly activeTab: ExtensionSidebarTab;
    readonly isClientQuickCalloutBarVisible: boolean;
    readonly isAgentChatBarVisible: boolean;
}) {
    return cn(
        'p-4',
        activeTab === 'outreaches' && isClientQuickCalloutBarVisible
            ? 'pb-28'
            : null,
        isAgentChatBarVisible && !isClientQuickCalloutBarVisible && 'pb-20',
    );
}

function AuthenticatedExtensionPage() {
    const posthog = usePostHog();
    const { timezone } = useOrganizationTimezone();
    const { data: user, isLoading: isLoadingUser } = useUser({
        reason: 'extension-sidebar',
    });
    const {
        organizationId: selectedOrganizationId,
        memberships,
        isLoading: isLoadingOrganizationContext,
    } = useOrganizationContext();
    const userId = user?.id ?? null;
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
    const hasTrackedSidebarOpenRef = useRef(false);
    const lastPostedOutreachCountKeyRef = useRef<string | null>(null);
    const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());
    const [ehrSelectedShifts, setEhrSelectedShifts] = useState<
        Array<SelectedExtensionShift>
    >([]);
    const [pageShifts, setPageShifts] = useState<Array<ExtensionPageShift>>([]);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [organizationSearch, setOrganizationSearch] = useState('');
    const [openOrganizationPickerId, setOpenOrganizationPickerId] = useState<
        string | null
    >(null);
    const [isFeedControlsOpen, setIsFeedControlsOpen] = useState(false);
    const [sidepanelConfig, setSidepanelConfig] =
        useState<ExtensionSidepanelConfigPayload | null>(null);

    useEffect(() => {
        logExtensionPageEvent('Authenticated extension page mounted');
        return () => {
            logExtensionPageEvent('Authenticated extension page unmounted');
        };
    }, []);

    const prefs = useExtensionSidebarPrefs(
        userId,
        sidepanelConfig?.syncSidebarOpenStateByDomain,
    );
    const extensionOrganization = useExtensionOrganization({
        userId,
        userIsAdmin: user?.admin === true,
        isLoadingUser,
        isLoadingOrganizationContext,
        memberships,
        selectedOrganizationId,
        pageInfo,
    });
    const {
        resolvedOrganizationId,
        extensionSelectedOrganizationId,
        fixtureOrganizationId,
        matchedOrganizationId,
    } = extensionOrganization;
    const {
        data: homeFeed,
        error: homeFeedError,
        isLoading: isLoadingHomeFeed,
        mutate: mutateHomeFeed,
        revalidate: revalidateHomeFeed,
    } = useHomeFeed(resolvedOrganizationId, {
        reason: 'extension-sidebar',
    });
    const { data: extensionConfig } = useExtensionConfig(
        resolvedOrganizationId,
        { reason: 'extension-config' },
    );
    const { data: organization } = useOrganization(resolvedOrganizationId, {
        reason: 'extension-page',
    });
    const ownershipEnabled = useFeatureFlag(
        'conversation_ownership_enabled',
        organization?.features,
    );
    // The extension resolves its own organization, so read the name-order
    // setting from that record rather than the app-wide selection.
    const displayNamesLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;
    const resolvedOrganizationName = getResolvedOrganizationName(
        organization,
        extensionConfig,
        extensionOrganization,
    );

    useOrganizationEvents(resolvedOrganizationId, userId, {
        browserNotificationsEnabled: true,
        notificationClickBehavior: 'new-tab',
        telemetrySurface: 'extension',
    });

    useEffect(() => {
        logExtensionPageEvent('Organization resolution state changed', {
            isLoadingUser,
            isLoadingOrganizationContext,
            userId,
            extensionSelectedOrganizationId,
            selectedOrganizationId,
            fixtureOrganizationId,
            matchedOrganizationId,
            resolvedOrganizationId,
            membershipCount: memberships.length,
            pageInfoProvider: pageInfo?.provider ?? null,
            pageInfoUrl: pageInfo?.url ?? null,
        });
    }, [
        extensionSelectedOrganizationId,
        fixtureOrganizationId,
        isLoadingOrganizationContext,
        isLoadingUser,
        matchedOrganizationId,
        memberships.length,
        pageInfo?.provider,
        pageInfo?.url,
        resolvedOrganizationId,
        selectedOrganizationId,
        userId,
    ]);

    useEffect(() => {
        logExtensionPageEvent('Home feed state changed', {
            isLoadingHomeFeed,
            outreachCount: homeFeed?.outreaches.length ?? null,
            error: describeUnknownError(homeFeedError),
        });
    }, [homeFeed?.outreaches.length, homeFeedError, isLoadingHomeFeed]);

    const resolvedContext = useExtensionResolvedContext(
        resolvedOrganizationId,
        pageInfo,
    );
    const { resolvedShiftId, resolvedClientName } = resolvedContext;
    // General agent chat surface inside the extension. Follows the same
    // lifecycle gate as the in-app general sidebar agent; only enabled-stage
    // orgs get it (an unset/loading stage stays disabled).
    const isExtensionAgentChatEnabled = isGeneralAgentLifecycleEligible(
        organization?.customer_lifecycle_stage,
    );
    const agentChat = useExtensionAgentChat(resolvedOrganizationId);
    const isInitializing =
        isLoadingUser ||
        isLoadingOrganizationContext ||
        (!resolvedOrganizationId && memberships.length === 0);
    const ehrSelectedShiftResolution = useEhrSelectedShiftResolution({
        ehrSelectedShifts,
        resolvedOrganizationId,
        // Null on old extension shells that don't forward their manifest
        // version through `EXTENSION_SIDEPANEL_CONFIG`. Used to tag Sentry
        // captures so we can attribute issues to specific extension builds.
        extensionVersion: sidepanelConfig?.extensionVersion ?? null,
        isInitializing,
    });
    const pageShiftResolvedMap = usePageShiftResolvedMap(
        pageShifts,
        resolvedOrganizationId,
        userId,
    );
    const openInPhoebe = useOpenInPhoebe(posthog, resolvedOrganizationId);
    const contextualShift = useContextualShift({
        resolvedOrganizationId,
        resolvedShiftId,
        pageInfo,
        homeFeedOutreaches: homeFeed?.outreaches,
        currentTimeMs,
        posthog,
        openInPhoebe,
    });
    const clientShiftContext = useClientShiftContext({
        resolvedOrganizationId,
        resolvedClientId: resolvedContext.resolvedClientId,
        resolvedClientName,
        pageInfo,
        currentTimeMs,
    });
    const conversations = useSidebarConversations({
        resolvedOrganizationId,
        resolvedCaregiverId: resolvedContext.resolvedCaregiverId,
        hasCaregiverContext: resolvedContext.hasCaregiverContext,
    });
    const outreachFeed = useExtensionOutreachFeed({
        homeFeedOutreaches: homeFeed?.outreaches,
        pageInfo,
        timezone,
        showOnlyMineFeedFilter: prefs.showOnlyMineFeedFilter,
        userId: user?.id,
        resolvedClientName,
        contextualShiftFeedItem: contextualShift.contextualShiftFeedItem,
        feedSort: prefs.feedSort,
    });
    const clientSync = useClientSync({
        resolvedOrganizationId,
        resolveContextRequest: resolvedContext.resolveContextRequest,
        pageInfo,
        mutateResolvedContext: resolvedContext.mutateResolvedContext,
    });
    const sidebarRefresh = useSidebarRefresh({
        mutateHomeFeed,
        revalidateHomeFeed,
        resolvedOrganizationId,
        resolvedShiftId,
        resolvedClientName,
        mutateContextualOutreaches: contextualShift.mutateContextualOutreaches,
        mutateClientShiftSearch: clientShiftContext.mutateClientShiftSearch,
        mutateClientCandidateOutreaches:
            clientShiftContext.mutateClientCandidateOutreaches,
    });
    usePublishedShiftActions(
        contextualShift,
        outreachFeed.feedShiftActionUpdates,
    );

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setCurrentTimeMs(Date.now());
        }, CLIENT_SHIFT_TIME_REFRESH_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);

    const selectedClientShifts = useSelectedClientShiftIds(
        clientShiftContext.clientEligibleShiftIds,
    );
    const navigation = useExtensionSidebarNavigation({
        pageInfo,
        pageShiftCount: pageShifts.length,
        ehrSelectedShiftCount: ehrSelectedShifts.length,
        hasCaregiverContext: resolvedContext.hasCaregiverContext,
        resolvedOrganizationId,
        conversations,
    });
    useSidebarRealtimeRefresh(resolvedOrganizationId, sidebarRefresh);
    useExtensionParentMessages({
        posthog,
        pageInfo,
        setPageInfo,
        setEhrSelectedShifts,
        setSidepanelConfig,
        setPageShifts,
    });

    useEffect(() => {
        if (hasTrackedSidebarOpenRef.current || !posthog) {
            return;
        }

        hasTrackedSidebarOpenRef.current = true;
        posthog?.capture(EVENTS.EXTENSION_SIDEBAR_OPENED);
    }, [posthog]);

    useEffect(() => {
        if (!resolvedOrganizationId || !user) {
            return;
        }

        const runtimeConfig = getRuntimeConfig();
        const resolvedPhoebeAppUrl = window.location.origin;
        logExtensionPageEvent('Posting EXTENSION_CONFIG', {
            organizationId: resolvedOrganizationId,
            organizationName: resolvedOrganizationName,
            userId: user.id,
            allowedDomainCount: extensionConfig?.allowed_domains.length ?? 0,
        });
        window.parent.postMessage(
            {
                type: 'EXTENSION_CONFIG',
                payload: {
                    phoebeAppUrl: resolvedPhoebeAppUrl,
                    kratosPublicUrl: new URL(
                        runtimeConfig.kratosPublicUrl,
                        resolvedPhoebeAppUrl,
                    ).toString(),
                    organizationId: resolvedOrganizationId,
                    organizationName: resolvedOrganizationName,
                    userId: user.id,
                    userName: getUserName(user),
                    apiUrl: new URL(
                        runtimeConfig.apiUrl,
                        resolvedPhoebeAppUrl,
                    ).toString(),
                    allowedDomains: extensionConfig?.allowed_domains ?? [],
                },
            },
            '*',
        );
    }, [
        extensionConfig,
        resolvedOrganizationName,
        resolvedOrganizationId,
        user,
    ]);

    usePublishExtensionSidebarPrefs(prefs);

    const { activeBottomBarOutreaches } = outreachFeed;
    useEffect(() => {
        if (!EXTENSION_BOTTOM_BAR_ENABLED) {
            return;
        }

        const count = activeBottomBarOutreaches.length;
        const actionableCount = activeBottomBarOutreaches.filter(
            (item) => item.needs_action,
        ).length;
        const nextCountKey = `${count}:${actionableCount}`;
        if (lastPostedOutreachCountKeyRef.current === nextCountKey) {
            return;
        }

        lastPostedOutreachCountKeyRef.current = nextCountKey;
        logExtensionPageEvent('Posting EXTENSION_OUTREACH_COUNT', {
            count,
            actionableCount,
        });
        window.parent.postMessage(
            {
                type: 'EXTENSION_OUTREACH_COUNT',
                count,
                actionableCount,
            },
            '*',
        );
    }, [activeBottomBarOutreaches]);

    useExtensionContextAnalytics({
        posthog,
        homeFeed,
        pageInfo,
        resolvedOrganizationId,
        resolvedShiftId,
        resolvedClientName,
        isLoadingResolvedContext: resolvedContext.isLoadingResolvedContext,
        contextualShift,
        clientShiftContext,
        contextualClientFeedItemsForAnalytics:
            outreachFeed.contextualClientFeedItemsForAnalytics,
    });
    const pageShiftSelection = usePageShiftSelection({
        pageInfo,
        pageShifts,
        ehrSelectedShifts,
        setEhrSelectedShifts,
        contextualShift,
    });

    if (!isInitializing && (!user || !resolvedOrganizationId)) {
        return <ExtensionInitializationError />;
    }

    const pickerState = {
        organization: extensionOrganization,
        openPickerId: openOrganizationPickerId,
        setOpenPickerId: setOpenOrganizationPickerId,
        search: organizationSearch,
        setSearch: setOrganizationSearch,
    };
    const pageShiftCardsContext = {
        pageShiftSelection,
        pageShiftResolvedMap,
        timezone,
    };
    const { shouldShowSelectableClientShiftCards } = clientShiftContext;
    const selectedShiftsSection = (
        <ExtensionSelectedShiftsSection
            ehrSelectedShifts={ehrSelectedShifts}
            isExtensionAgentChatEnabled={isExtensionAgentChatEnabled}
            onOpenAgentChat={agentChat.openAgentChat}
            onSelectOrganization={
                extensionOrganization.handleSelectExtensionOrganization
            }
            openCreateOutreachInPhoebe={openInPhoebe.openCreateOutreachInPhoebe}
            pageShiftSelection={pageShiftSelection}
            pageShifts={pageShifts}
            resolution={ehrSelectedShiftResolution}
            resolvedOrganizationId={resolvedOrganizationId}
            timezone={timezone}
        />
    );

    return (
        <DisplayNamesLastFirstContext.Provider value={displayNamesLastFirst}>
            <div className="bg-muted/20 relative min-h-screen">
                <ExtensionSidebarHeader
                    onOpenSettings={() => {
                        setIsSettingsOpen(true);
                    }}
                    openPhoebePathInTab={openInPhoebe.openPhoebePathInTab}
                    organization={organization}
                    pickerState={pickerState}
                    sidepanelConfig={sidepanelConfig}
                    timezone={timezone}
                    userId={user?.id}
                />
                <ExtensionSettingsDialog
                    isOpen={isSettingsOpen}
                    onOpenChange={(open) => {
                        setIsSettingsOpen(open);
                        if (!open) {
                            setOpenOrganizationPickerId(null);
                        }
                    }}
                    pickerState={pickerState}
                    prefs={prefs}
                    resolvedOrganizationName={resolvedOrganizationName}
                />
                <main
                    className={getMainClassName({
                        activeTab: navigation.activeTab,
                        isClientQuickCalloutBarVisible:
                            shouldShowSelectableClientShiftCards,
                        isAgentChatBarVisible:
                            isExtensionAgentChatEnabled &&
                            !agentChat.isAgentChatOpen,
                    })}
                >
                    <Tabs
                        className="gap-4"
                        onValueChange={navigation.handleTabChange}
                        value={navigation.activeTab}
                    >
                        <TabsList className="bg-muted grid h-auto w-full grid-cols-3 rounded-2xl p-1">
                            <TabsTrigger
                                className="rounded-xl text-sm"
                                value="page_shifts"
                            >
                                Shifts
                            </TabsTrigger>
                            <TabsTrigger
                                className="rounded-xl text-sm"
                                value="outreaches"
                            >
                                Outreaches
                            </TabsTrigger>
                            <TabsTrigger
                                className="rounded-xl text-sm"
                                value="conversations"
                            >
                                Conversations
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent className="space-y-4" value="outreaches">
                            <ExtensionOutreachesTab
                                clientShiftContext={clientShiftContext}
                                clientSync={clientSync}
                                contextualShift={contextualShift}
                                feedControls={{
                                    isOpen: isFeedControlsOpen,
                                    setIsOpen: setIsFeedControlsOpen,
                                }}
                                homeFeedState={{
                                    error: homeFeedError,
                                    isLoading:
                                        isInitializing || isLoadingHomeFeed,
                                }}
                                openInPhoebe={openInPhoebe}
                                outreachFeed={outreachFeed}
                                pageInfo={pageInfo}
                                pageShiftCardsContext={pageShiftCardsContext}
                                pageShiftSelection={pageShiftSelection}
                                posthog={posthog}
                                prefs={prefs}
                                resolvedContext={resolvedContext}
                                resolvedOrganizationId={resolvedOrganizationId}
                                selectedClientShifts={selectedClientShifts}
                                selectedShiftsSection={selectedShiftsSection}
                                userId={user?.id}
                            />
                        </TabsContent>
                        <TabsContent className="space-y-4" value="page_shifts">
                            <ExtensionPageShiftsTab
                                context={pageShiftCardsContext}
                                hasSelectedShifts={ehrSelectedShifts.length > 0}
                                isActive={
                                    navigation.activeTab === 'page_shifts'
                                }
                                openCreateOutreachInPhoebe={
                                    openInPhoebe.openCreateOutreachInPhoebe
                                }
                                pageInfo={pageInfo}
                                pageShifts={pageShifts}
                                refreshSignal={
                                    sidebarRefresh.extensionShiftRangeRefreshSignal
                                }
                                resolvedOrganizationId={resolvedOrganizationId}
                                selectedShiftsSection={selectedShiftsSection}
                            />
                        </TabsContent>
                        <TabsContent
                            className="space-y-4"
                            value="conversations"
                        >
                            <ExtensionConversationWorkspace
                                conversations={conversations}
                                navigation={navigation}
                                openConversationInPhoebe={
                                    openInPhoebe.openConversationInPhoebe
                                }
                                ownershipEnabled={ownershipEnabled}
                                resolvedOrganizationId={resolvedOrganizationId}
                            />
                        </TabsContent>
                    </Tabs>
                </main>
                {isExtensionAgentChatEnabled ? (
                    <ExtensionAgentChat
                        agentChat={agentChat}
                        isBottomBarHidden={shouldShowSelectableClientShiftCards}
                        openPhoebePathInTab={openInPhoebe.openPhoebePathInTab}
                        resolvedOrganizationId={resolvedOrganizationId}
                    />
                ) : null}
            </div>
        </DisplayNamesLastFirstContext.Provider>
    );
}
